import React, { useEffect, useMemo, useState } from 'react';
import {
  EMPTY,
  fromEvent,
  merge,
  timer,
} from 'rxjs';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  filter, finalize, map, tap,
} from 'rxjs/operators';
import { useObservable } from 'react-use';
import { liveStreamActions, liveStreamSelectors } from 'models/liveStream';
import { selectXtraEvent } from 'models/cms/events';
import {
  LIVESTREAM_REALTIMEDB_API_URL,
  LIVESTREAM_TIMESTAMP_MAX_WAIT,
  LIVESTREAM_TIMESTAMP_UPDATE_INTERVAL,
  PROXY_WORKER_URL,
} from 'appenv';
import remoteTimeNow, { isRemoteTimeAccurate } from 'libs/remote-time-now';

/**
 * @param targetBoothId The id of the subscribing booth, undefined to subscribe to all booths
 */
const useLiveStreamStatusSubscriber = (targetBoothId?: string) => {
  const dispatch = useDispatch();
  const eventId = useSelector(selectXtraEvent)?.id;
  const subscribeToAllBooth = targetBoothId == null;

  const boothsLiveStreamingLastTimeStampUpdateSource = useMemo(() => (window.EventSource
    ? new EventSource(`${PROXY_WORKER_URL}/events/${eventId}/channels/booths${subscribeToAllBooth ? '' : `/${targetBoothId}`}.json?eventx_url=${LIVESTREAM_REALTIMEDB_API_URL}`) : null
  ), [eventId, subscribeToAllBooth, targetBoothId]);
  const boothsLiveStreamingLastTimeStampUpdate$ = useMemo(() => (boothsLiveStreamingLastTimeStampUpdateSource == null ? EMPTY
    : merge(
      fromEvent<any>(boothsLiveStreamingLastTimeStampUpdateSource, 'patch'),
      fromEvent<any>(boothsLiveStreamingLastTimeStampUpdateSource, 'put'),
    ).pipe(
      tap((event: MessageEvent) => {
        const payload: {data?:any, path: string} = JSON.parse(event.data);
        const boothId = subscribeToAllBooth ? payload.path?.split('/')?.[1] : targetBoothId;
        const data = payload?.data;

        function updateBoothStatus(updatingBoothId: string, isStop: boolean) {
          dispatch(liveStreamActions.updateBoothLiveStreamingLastTimeStamp({
            boothId: updatingBoothId,
            streamingLastTimeStamp: isStop ? null : new Date().toISOString(),
          }));
          dispatch(liveStreamActions.updateBoothLiveStreamingStatus({
            boothId: updatingBoothId,
            streamingStatus: !isStop,
          }));
        }

        /**
         * The booth will be consider as "explicity ended" if the booth id not exist in the dict
         * The live status of "explicit ended" booth will be clear immediately
         */
        function initCleanUpExplicitLiveEndedBooths(timeStamps: Record<string, {lastStreamingTimeStamp?:string}> | null) {
          const cleanUpExcludeBoothIds = timeStamps == null ? [] : Object.keys(timeStamps);
          dispatch(liveStreamActions.cleanUpBoothLiveStreamingStatus({ excludeBoothIds: cleanUpExcludeBoothIds }));
        }

        /**
         * If the remote time is fetch from remote server, then we can trust it is accurate and do the following checking
         *
         * If last update + update interval > current time, consider it is live and during status update gap
         */
        function initRenewBoothLiveStatusByTimestamp(timeStamps: Record<string, {lastStreamingTimeStamp?:string}> | null) {
          if (!isRemoteTimeAccurate() || timeStamps == null) return;

          const remoteTime = remoteTimeNow().toDate();
          Object.entries(timeStamps).forEach(([entryBoothId, entryData]: [string, { lastStreamingTimeStamp?: string }]) => {
            if (entryData?.lastStreamingTimeStamp && new Date(new Date(entryData.lastStreamingTimeStamp).getTime() + LIVESTREAM_TIMESTAMP_UPDATE_INTERVAL) > remoteTime) {
              updateBoothStatus(entryBoothId, false);
            }
          });
        }

        const isAllBoothsInitialStatus = subscribeToAllBooth && event.type === 'put' && boothId === '';
        if (isAllBoothsInitialStatus) {
          batch(() => {
            initCleanUpExplicitLiveEndedBooths(data as Record<string, {lastStreamingTimeStamp?:string}> | null);
            initRenewBoothLiveStatusByTimestamp(data as Record<string, {lastStreamingTimeStamp?:string}> | null);
          });
        } else {
          const isStopStreamingSignal = data?.lastStreamingTimeStamp == null;
          batch(() => updateBoothStatus(boothId, isStopStreamingSignal));
        }
      }),
      finalize(() => boothsLiveStreamingLastTimeStampUpdateSource.close()),
    )), [boothsLiveStreamingLastTimeStampUpdateSource, dispatch, subscribeToAllBooth, targetBoothId]);
  useObservable(boothsLiveStreamingLastTimeStampUpdate$);

  const [nextUpdateTime, setNextUpdateTime] = useState(0);

  const currentTime$ = useMemo(() => timer(0, 100).pipe(map(() => new Date().getTime())), []);
  const currentTime = useObservable(currentTime$);

  const boothLiveStreamingLastTimeStamp = useSelector(liveStreamSelectors.selectBoothLiveStreamingLastTimeStamp);

  useEffect(() => {
    if (currentTime > nextUpdateTime) {
      batch(() => {
        Object.entries(boothLiveStreamingLastTimeStamp).forEach(([boothId, isoTime]) => {
          if (new Date(new Date(isoTime).getTime() + LIVESTREAM_TIMESTAMP_MAX_WAIT) < new Date()) {
            dispatch(liveStreamActions.updateBoothLiveStreamingLastTimeStamp({
              boothId,
              streamingLastTimeStamp: null,
            }));
            dispatch(liveStreamActions.updateBoothLiveStreamingStatus({
              boothId,
              streamingStatus: false,
            }));
          }
        });
      });

      setNextUpdateTime(currentTime + 1000);
    }
  }, [boothLiveStreamingLastTimeStamp, currentTime, dispatch, nextUpdateTime]);
};

export default useLiveStreamStatusSubscriber;
